import chalk from 'chalk';
import ora from 'ora';
import {
  CHANNEL_REGISTRY,
  getChannel,
  resolveChannels,
  type ChannelId,
  type ChannelPolicy,
  type OrgChannelPolicy,
} from '@augmented/core';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

/**
 * `agt channels list`
 */
export function channelsListCommand(): void {
  const json = isJsonMode();

  if (json) {
    const channels = CHANNEL_REGISTRY.map((ch) => ({
      id: ch.id,
      name: ch.name,
      security_tier: ch.securityTier,
      e2e_encrypted: ch.e2eEncrypted,
      audit_trail: ch.auditTrail,
      public_exposure_risk: ch.publicExposureRisk,
    }));
    jsonOutput({ ok: true, channels });
    return;
  }

  console.log(chalk.bold('\nChannel Registry (18 channels)\n'));

  const rows = CHANNEL_REGISTRY.map((ch) => [
    ch.id,
    ch.name,
    ch.securityTier === 'elevated'
      ? chalk.green(ch.securityTier)
      : ch.securityTier === 'limited'
        ? chalk.red(ch.securityTier)
        : chalk.cyan(ch.securityTier),
    typeof ch.e2eEncrypted === 'boolean'
      ? (ch.e2eEncrypted ? chalk.green('yes') : chalk.dim('no'))
      : chalk.yellow(ch.e2eEncrypted),
    typeof ch.auditTrail === 'boolean'
      ? (ch.auditTrail ? chalk.green('yes') : chalk.dim('no'))
      : chalk.yellow(ch.auditTrail),
    ch.publicExposureRisk === 'High'
      ? chalk.red(ch.publicExposureRisk)
      : ch.publicExposureRisk === 'Medium'
        ? chalk.yellow(ch.publicExposureRisk)
        : chalk.green(ch.publicExposureRisk),
  ]);

  table(['ID', 'Name', 'Security Tier', 'E2E Encrypted', 'Audit Trail', 'Public Risk'], rows);
}

/**
 * `agt channels check <agent>`
 */
export async function channelsCheckCommand(agentCodeName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Resolving channels for "${agentCodeName}"…`, isSilent: json });
  spinner.start();

  try {
    const data = await api.get<{
      agent_channels: string[];
      channel_policy: {
        team_id: string;
        allowed_channels: string[];
        denied_channels: string[];
        require_elevated_for_pii: boolean;
      } | null;
    }>(`/agents/${encodeURIComponent(agentCodeName)}/channels`);

    const agentPolicy: ChannelPolicy = {
      policy: 'allowlist',
      allowed: (data.agent_channels ?? []) as ChannelId[],
      denied: [],
      require_approval_to_change: false,
    };

    const orgPolicy: OrgChannelPolicy | undefined = data.channel_policy
      ? {
          organization_id: data.channel_policy.team_id,
          allowed_channels: (data.channel_policy.allowed_channels ?? []) as ChannelId[],
          denied_channels: (data.channel_policy.denied_channels ?? []) as ChannelId[],
          require_elevated_for_pii: data.channel_policy.require_elevated_for_pii ?? false,
        }
      : undefined;

    const resolved = resolveChannels(agentPolicy, orgPolicy);

    spinner.stop();

    if (json) {
      jsonOutput({
        ok: true,
        agent: agentCodeName,
        agent_allowlist: agentPolicy.allowed,
        org_policy: orgPolicy
          ? {
              allowed: orgPolicy.allowed_channels,
              denied: orgPolicy.denied_channels,
              require_elevated_for_pii: orgPolicy.require_elevated_for_pii,
            }
          : null,
        resolved_channels: resolved,
        count: resolved.length,
      });
      return;
    }

    console.log(chalk.bold(`\nChannel Resolution: ${agentCodeName}\n`));

    info(`Agent allowlist: ${agentPolicy.allowed.length > 0 ? agentPolicy.allowed.join(', ') : 'none'}`);

    if (orgPolicy) {
      info(`Org allowed: ${orgPolicy.allowed_channels.length > 0 ? orgPolicy.allowed_channels.join(', ') : 'all (no restriction)'}`);
      info(`Org denied:  ${orgPolicy.denied_channels.length > 0 ? orgPolicy.denied_channels.join(', ') : 'none'}`);
      if (orgPolicy.require_elevated_for_pii) {
        info('Org requires elevated tier for PII channels');
      }
    } else {
      info('No org channel policy configured.');
    }

    console.log();

    if (resolved.length === 0) {
      error('No channels available after resolution. The agent cannot communicate.');
      process.exitCode = 1;
      return;
    }

    const rows = resolved.map((chId) => {
      const ch = getChannel(chId);
      return [
        chId,
        ch?.name ?? 'Unknown',
        ch?.securityTier ?? '-',
        ch?.publicExposureRisk ?? '-',
      ];
    });

    success(`${resolved.length} channel(s) available:`);
    table(['ID', 'Name', 'Security Tier', 'Public Risk'], rows);
  } catch (err) {
    spinner.fail('Failed to resolve channels.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}
